<template>
  <b-modal
    id="modal_edit_purchase_order"
    :title="$t('Edição Parcial do Pedido')"
    no-close-on-backdrop
    hide-header-close
    size="xl"
    scrollable
    centered
    @hidden="hideModal"
  >
    <template #default>
      <FormulateForm 
        ref="formPartialEditing" 
        name="formPartialEditing"
      >
        <b-card-actions
          :title="$t('Pedido') + ' ' + form.purchaseOrderId + ' - ' + $t('Itens do Pedido')"
          :show-loading="fetching"
          no-actions
        >
          <b-table
            show-empty
            striped
            class="bordered"
            :fields="fields"
            :items="items"
          >
            <template #cell(selection)="row">
              <div class="d-flex justify-content-center align-items-center">
                <FormulateInput
                  :id="`modal_item-edit_check_${row.index}`"
                  v-model="row.item.willUpdate"
                  type="e-checkbox"
                  :disabled="integratedWithBeerCooler(row.item)"
                  @change="(val) => onSelectToUpdate(val, row.item)"
                />
                <small
                  v-if="integratedWithBeerCooler(row.item)"
                  class="text-muted mt-1 d-block text-center"
                >
                  {{ $t('Produto bloqueado.') }}
                </small>
              </div>
            </template>
            <template #cell(name)="{ item, index }">
              <div
                v-if="(!item.erpSku.sku || !item.erpSku.sku.name)"
                style="display: flex; margin-right: 5px"
              >
                <div style="width: 100%">
                  <FormulateInput
                    :id="`order-items-sku-searched-${index}`"
                    v-model="item.erpSku._skuBuffer"
                    type="select-searchable"
                    :placeholder="$t('Digite para pesquisar')"
                    :options="skusOptions"
                    autofocus
                    :instruction="
                      $t('Digite pelo menos 3 numeros ou letras para iniciar a pesquisa')
                    "
                    style="width: 100%; min-width: 200px; display: inline-block"
                    class="ml-50"
                    :validation="item.willUpdate ? 'required' : ''"
                    :validation-messages="{
                      required: $t('Obrigatório informar o produto'),
                    }"
                    @fetchSearch="fetchSearchSku"
                    @input="selectSkuBuffered(item, $event)"
                  />
                </div>
              </div>
              <div
                v-else
                style="display: flex"
              >
                <div
                  style="display: flex; align-items: center; margin: 3px 0;"
                >
                  <e-button
                    icon="trash"
                    variant="danger"
                    class="p-5"
                    :title="$t('Remover associação')"
                    :disabled="!item.willUpdate"
                    @click="removeSkuAssociation(item.erpSku)"
                  />
                </div>
                <e-product-router-link
                  :sku-id="item.erpSku.sku.id"
                  :product-id="item.erpSku.sku.productId"
                  :product-name="item.erpSku.sku.name"
                  :ean="item.erpSku.sku.ean"
                />
              </div>
            </template>
            <template #cell(quantity)="row">
              <div style="display: flex; flex-direction: column; align-items: center">
                <FormulateInput
                  :id="`order-items-quantity-${row.index}`"
                  v-model="row.item.erpSku.quantity"
                  type="text-number"
                  :precision="3"
                  class="text-right"
                  :title="$t('Clique para editar')"
                  validation="required|min:0.001"
                  :validation-messages="{
                    required: $t('Obrigatório'),
                    min: $t('Deve ser maior que zero'),
                  }"
                  style="width: 80px"
                  :disabled="!row.item.willUpdate"
                />
              </div>
            </template>
          </b-table>

        </b-card-actions>
      </FormulateForm>
    </template>
    <template #modal-footer>
      <b-row class="w-100 d-flex justify-content-between align-items-center">
        <b-col class="d-flex justify-content-end">
          <e-button
            id="modal_return_invoice_items-btn_cancel"
            class="mr-1"
            variant="outline-primary"
            :text="$t('Cancelar')"
            :text-shortcuts="['ESC']"
            @click="hideModal"
          />

          <e-button
            id="modal_return_invoice_items-btn_confirm"
            variant="primary"
            :text="$t('Confirmar')"
            :busy="busy"
            :disabled="!canConfirmEdits"
            @click="onConfirm"
          />
        </b-col>
      </b-row>
    </template>
  </b-modal>
</template>

<script>
import { BModal, BRow, BCol, BTable } from 'bootstrap-vue'
import BCardActions from '@/@core/components/b-card-actions/BCardActions.vue'
import { EButton, EProductRouterLink } from '@/views/components'
import delegablePermissions from '@/utils/delegable-permissions'
import { alerts, purchaseOrderDomains } from '@/mixins'
import { getApiErrorData, getApiErrorDataMessage } from '@/utils/error-utils'
import { mapActions, mapGetters, mapState } from 'vuex'

const getInitialModalItem = () => ({
  willUpdate: false,
  skuProduct: { name: '' },
  quantity: 0,
})

const getInitialModalForm = () => ({
  purchaseOrderId: null,
  items: [],
})

export default {
  name: 'ModalEditPurchaseOrder',
  components: { BModal, BRow, BCol, BCardActions, EButton, BTable, EProductRouterLink },
  mixins: [alerts, purchaseOrderDomains],
  data() {
    return {
      form: getInitialModalForm(),
      busy: false,
      fetching: false,
      purchaseOrder: null,
      skus: [],
      showAverageCostDiffWarning: false,
      delegateUserData: null,
    }
  },
  computed: {
    ...mapState('app', ['modalAuthRef']),
    ...mapGetters('pages/purchase/purchaseOrder/purchaseOrderMaintain', ['readOnly']),
    canConfirmEdits() {
      return this.form.items.some(i => i.willUpdate)
    },
    skusOptions() {
      return this.skus.map(sku => ({
        ...sku,
        value: sku.id,
        label: sku.name,
      }))
    },
    fields() {
      return [
        {
          label: this.$t('Editar'),
          key: 'selection',
          thClass: 'text-center',
          tdClass: 'text-center',
          thStyle: { width: '100px' },
        },
        {
          label: this.$t('Nome do Produto'),
          key: 'name',
          thClass: 'text-center',
          tdClass: 'text-left',
        },
        {
          label: this.$t('Qtd. de Entrada'),
          key: 'quantity',
          thClass: 'text-center',
          tdClass: 'text-center',
          thStyle: { width: '100px' },
        },
      ]
    },
    items() {
      return this.form.items
    },
  },
  methods: {
    ...mapActions('pages/purchase/purchaseOrder/purchaseOrderMaintain', {
      stFetchPurchaseOrderById: 'fetchPurchaseOrderById',
    }),
    ...mapActions('pages/purchase/purchaseOrder/purchaseOrderMaintain', ['partialUpdatePurchaseOrder']),

    hideModal() {
      this.resetModal()
      this.$bvModal.hide('modal_edit_purchase_order')
    },

    resetModal() {
      this.purchaseOrder = null
    },

    async show({ purchaseOrderId }) {
      this.$bvModal.show('modal_edit_purchase_order')

      await new Promise(resolve => setTimeout(() => resolve(), 151))

      try {
        this.fetching = true
        this.busy = true

        const data = await this.stFetchPurchaseOrderById(purchaseOrderId)
        this.purchaseOrder = data

        const itemsFormated = data.items.map(i => ({
          ...getInitialModalItem(),
          ...i,
          erpSku: {
          ...i.erpSku,
          _skuBuffer: null,
          },
          skuProduct: { ...i.erpSku?.sku },
          quantity: i.erpSku.quantity,
        }))
        
        this.form = {
          ...getInitialModalForm(),
          purchaseOrderId: data.id,
          items: itemsFormated,
        }
      } catch (error) {
        this.hideModal()
        throw error
      } finally {
        this.fetching = false
        this.busy = false
      }
    },

    async onConfirm() {
      try {
        this.busy = true

        this.$refs.formPartialEditing.showErrors()
        if (!this.isValidInputs()) return

        const confirm = await this.confirm({
          title: this.$t('Confirma a alteração do pedido?'),
          text: this.$t(
            'Os itens do pedido serão alterados mas a parte financeira não sofrerá alterações. Deseja continuar?',
          ),
        })
        if (!confirm) return

        await this.partialUpdatePurchaseOrder({ payload: this.form })

        this.actionsAfterSave()
      } catch (error) {
        if (Array.isArray(error.response?.data?.Message)) {
          await this.confirmRevision(error.response?.data?.Message[0])
        } else {
          const errorData = getApiErrorData(error)
          if (errorData.message === 'PURCHASE_ORDER.MANUAL_ERROR.HAS_DEACTIVATED_ITEMS')
            this.showError({ message: getApiErrorDataMessage(errorData) })
          else this.showError({ error })
        }
      } finally {
        this.busy = false
      }
    },

    async confirmRevision(errorMessage) {
      const htmlMessage = this.mxGetAverageCostDiffAlertMsg(errorMessage)

      if (!this.showAverageCostDiffWarning) {
        await this.confirmHtml({
          title: this.$t('Atenção'),
          html: htmlMessage,
          confirmButtonText: this.$t('Ok'),
          showCancelButton: false,
        })
        this.showAverageCostDiffWarning = true
        return
      }

      try {
        const confirmed = await this.confirmHtml({
          title: this.$t('Atenção'),
          html: htmlMessage,
          confirmButtonText: this.$t('Prosseguir, eu já revisei'),
          cancelButtonText: this.$t('Revisar'),
          focusCancel: true,
        })

        if (confirmed) {
          this.delegateUserData = await this.modalAuthRef.auth(
            delegablePermissions.ERP_PURCHASE_ORDER_DIFF_AVERAGE_PRICE,
            this.purchaseOrder?.store?.id
          )
          
          await this.partialUpdatePurchaseOrder({
            payload: this.form,
            itemsUnitValueRevision: true,
            tokenDelegated: this.delegateUserData?.token
          })

          this.actionsAfterSave()
        }
      } catch (error) {
        if (error.name !== 'Cancel') {
          this.showError({ error })
        }
      }
    },

    actionsAfterSave() {
      this.showSuccess({ message: this.$t('Pedido atualizado com sucesso!') })

      this.hideModal()
    },

    isValidInputs() {
      if (this.form.items.some(i => i.willUpdate && !i.erpSku.sku)) {
        this.showInvalidDataMessage({
          message: this.$t('O produto é obrigatório para os itens que serão editados'),
        })
        return false
      }
      
      if (this.form.items.some(i => i.willUpdate && (!i.erpSku.quantity || i.erpSku.quantity <= 0))) {
        this.showInvalidDataMessage({
          message: this.$t('Alguns items estão com a quantidade inválida'),
        })
        return false
      }

      return true
    },

    async fetchSearchSku(loading, search) {
      try {
        loading(true)
        this.skus = (
          await this.$http.get('/api/skus/search', {
            params: {
              search,
              active: true,
              includeFixedAssets: true,
            },
          })
        ).data.results
      } catch (e) {
        this.showError({
          message: this.$t('Não foi possível fazer a pesquisa'),
        })
      } finally {
        loading(false)
      }
    },

    selectSkuBuffered(item, skuIdSelected) {
    const skuSelected = this.skus.find(
      sku => sku.id === parseInt(skuIdSelected, 10)
    )

      if (!skuSelected) return

      if (skuSelected.integrateWithBeerCooler) {
        this.showInvalidDataMessage({
          message: this.$t('Produtos EasyChopp não podem ser associados via edição parcial.'),
        })

        this.$nextTick(() => {
          this.$set(item.erpSku, '_skuBuffer', null)
        })

        return
      }

      this.$set(item.erpSku, 'sku', skuSelected)
    },

    selectSku(item, skuIdSelected) {
      const skuSelected = this.skus.find(sku => sku.id === parseInt(skuIdSelected, 10))
      
      if (skuSelected?.integrateWithBeerCooler) {
        this.showInvalidDataMessage({
          message: this.$t('Produtos EasyChopp não podem ser associados via edição parcial.'),
        })

        this.$set(item.erpSku, 'skuSearched', null)
        return
      }
     this.$set(item.erpSku, 'sku', skuSelected)
    },

    async removeSkuAssociation(erpSku) {
      if (
        await this.confirm({
          icon: 'question',
          text: this.$t('Tem certeza que deseja remover esta associação?'),
        })
      ) {
        this.$set(erpSku, 'sku', null)
        this.$set(erpSku, 'skuSearched', null)
      }
    },

    onSelectToUpdate(value, item) {
      if (!value) {
        this.$set(item.erpSku, 'sku', item.skuProduct)
        this.$set(item.erpSku, 'quantity', item.quantity)
      }
    },

    integratedWithBeerCooler(item) {
      return !!item?.erpSku?.sku?.integrateWithBeerCooler
    },
  },
}
</script>

<style lang="scss" scoped>
.fade-enter-active,
.fade-leave-active {
  transition: 0.2s;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
  transform: translateY(-20px);
}

.input-size {
  font-size: 1.4rem;
}
</style>